import { useState } from "react"
import { RibbonBackground } from "@/features/messenger"
import { Button } from "@/clientWeb/components/ui/button"
import { Home, RotateCcw, PhoneOff, Clock, ShieldAlert, Star, CheckCircle2, Send } from "lucide-react"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

/* ── Custom Animations ─────────────────────────────────────── */
const MeetingEndedStyles = () => (
  <style>{`
    @keyframes danger-pulse {
      0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); transform: scale(1); }
      70% { box-shadow: 0 0 0 15px rgba(239, 68, 68, 0); transform: scale(1.03); }
      100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); transform: scale(1); }
    }

    @keyframes primary-pulse {
      0% { box-shadow: 0 0 0 0 rgba(12, 90, 150, 0.2); transform: scale(1); }
      70% { box-shadow: 0 0 0 15px rgba(12, 90, 150, 0); transform: scale(1.03); }
      100% { box-shadow: 0 0 0 0 rgba(12, 90, 150, 0); transform: scale(1); }
    }

    @keyframes icon-shake {
      0%, 100% { transform: rotate(0deg); }
      25% { transform: rotate(-3deg); }
      75% { transform: rotate(3deg); }
    }

    .animate-danger-glow {
      animation: danger-pulse 2s infinite;
    }

    .animate-primary-glow {
      animation: primary-pulse 2.5s infinite;
    }

    .animate-soft-shake {
      animation: icon-shake 4s ease-in-out infinite;
    }

    .animate-container-entry {
      animation: fadeUp 0.5s ease-out both;
    }

    .star-pop {
      transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
    .star-pop:hover {
      transform: scale(1.15);
    }
    .star-pop:active {
      transform: scale(0.9);
    }
  `}</style>
)

export function MeetingEnded({
  reason = "ended",
  onRejoin,
  onGoHome,
  description,
  by,
}: {
  reason?: "ended" | "expired" | "removed" | "left"
  onRejoin?: () => void
  /** Omitted in an embedded meeting, where "the dashboard" is not ours to show. */
  onGoHome?: () => void
  /**
   * Says more than the reason's own sentence. The Screen Share page uses it
   * for a removal that is from screen sharing rather than from the meeting.
   */
  description?: string
  /**
   * The moderator who ended the meeting or removed this participant. With
   * several co-hosts, "The host has ended the meeting" did not say which.
   */
  by?: string | null
}) {
  const t = useTranslation();
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [feedbackState, setFeedbackState] = useState<"initial" | "details" | "submitted">("initial")
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [comment, setComment] = useState("")

  const titles: Record<typeof reason, string> = {
    ended: t("extraFiles.meetingEnded"),
    expired: t("extraFiles.sessionExpired"),
    removed: t("extraFiles.accessRevoked"),
    left: t("extraFiles.youveLeft"),
  }

  const subtitles: Record<typeof reason, string> = {
    ended: by ? t("extraFiles.endedByDesc", { name: by }) : t("extraFiles.endedDesc"),
    expired: t("extraFiles.expiredDesc"),
    removed: by ? t("extraFiles.removedByDesc", { name: by }) : t("extraFiles.removedDesc"),
    left: t("extraFiles.leftDesc"),
  }

  const isError = reason === "expired" || reason === "removed"
  
  const Icon =
    reason === "ended" ? PhoneOff :
    reason === "left" ? PhoneOff :
    reason === "expired" ? Clock :
    ShieldAlert

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])
  }

  const handleSubmitFeedback = () => {
    setFeedbackState("submitted")
  }

  return (
    <RibbonBackground>
      <MeetingEndedStyles />
      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-8 overflow-y-auto">
        
        {/* A column, not a scroll box.
        
            The whole card used to be one `max-h-[70vh] overflow-y-auto`, with
            the buttons at the bottom of it — so on anything but a tall window
            "Try to Rejoin" sat below the fold of an inner scroll area nobody
            has any reason to look for. It happened only for the reasons that
            carry the feedback block (leaving, and the meeting ending), which
            are the ones the button matters for, and it happened again the
            moment somebody rated the call: picking a star grows the block by a
            row of tags, a comment box and a Submit button, pushing the actions
            further out of sight. From a chair the option was simply not there.
        
            The rating scrolls now; what you can DO never leaves the card. */}
        <div className="flex w-full max-w-[28rem] max-h-[85vh] flex-col rounded-3xl bg-card backdrop-blur-xl p-8 sm:p-10 text-center shadow-2xl animate-container-entry border border-white/20">
          
          <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Animated Icon */}
          <div className="mx-auto mb-6 relative flex h-20 w-20 items-center justify-center">
            <div className={cn(
              "absolute inset-0 rounded-full",
              isError ? "bg-red-100 dark:bg-red-900/30 animate-danger-glow" : "bg-primary/10 dark:bg-primary/20 animate-primary-glow"
            )} />
            
            <div className={cn(
              "relative flex h-16 w-16 items-center justify-center rounded-full animate-soft-shake",
              isError ? "bg-red-500/10" : "bg-primary/10 dark:bg-primary/20"
            )}>
              <Icon 
                className={cn("h-8 w-8", isError ? "text-red-600 dark:text-red-500" : "text-primary dark:text-primary-400")} 
                strokeWidth={2} 
              />
              {/* Small status dot */}
              <div className={cn(
                "absolute top-2 right-2 h-3 w-3 rounded-full border-2 border-white dark:border-gray-900",
                isError ? "bg-red-600" : "bg-primary"
              )} />
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground dark:text-white tracking-tight">
            {titles[reason]}
          </h1>
          <p className="mt-2 text-sm sm:text-base leading-relaxed text-gray-900 dark:text-foreground/80 dark:text-gray-400">
            {description ?? subtitles[reason]}
          </p>

          {/* Rating / Feedback Section */}
          {!isError && (
            <div className="my-4 rounded-2xl bg-slate-50/80 dark:bg-card/5 border border-stroke-100 dark:border-border dark:border-white/10 p-5 sm:p-6 transition-all">
              {feedbackState === "submitted" ? (
                <div className="flex flex-col items-center justify-center py-4 space-y-3 animate-in fade-in zoom-in duration-300">
                  <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-1">
                    <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="font-semibold text-slate-900 dark:text-foreground/80 text-lg">{t("extraFiles.thanksForFeedback")}</p>
                  <p className="text-sm text-slate-500 dark:text-muted-foreground">{t("extraFiles.feedbackHelps")}</p>
                </div>
              ) : (
                <div className="animate-in fade-in duration-300">
                  <p className="font-semibold text-slate-800 dark:text-foreground mb-4 text-base">{t("extraFiles.howWasCallQuality")}</p>
                  <div className="flex justify-center gap-1 sm:gap-2 mb-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => { 
                          setRating(star); 
                          setFeedbackState("details"); 
                        }}
                        className="p-1 sm:p-1.5 star-pop outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
                      >
                        <Star
                          className={cn(
                            "h-5 w-5 sm:h-6 sm:w-6 transition-colors",
                            (hoverRating >= star || rating >= star)
                              ? "fill-amber-400 text-amber-400"
                              : "text-slate-300 dark:text-muted-foreground"
                          )}
                          strokeWidth={1.5}
                        />
                      </button>
                    ))}
                  </div>

                  {feedbackState === "details" && (
                    <div className="mt-6 space-y-5 animate-in slide-in-from-top-4 fade-in duration-300 text-left">
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-slate-500 dark:text-muted-foreground text-center uppercase tracking-wider">
                          {rating < 4 ? t("extraFiles.whatWentWrong") : t("extraFiles.whatWentWell")}
                        </p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {rating < 4 ? [
                            t("extraFiles.audioIssues"), t("extraFiles.videoIssues"), t("extraFiles.lagFreeze"), t("extraFiles.couldntHearOthers"), t("extraFiles.droppedConnection")
                          ].map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => handleTagToggle(tag)}
                              className={cn(
                                "text-[11px] sm:text-xs px-3 py-1.5 rounded-full border transition-colors",
                                selectedTags.includes(tag)
                                  ? "bg-primary text-white border-primary font-medium shadow-sm"
                                  : "bg-card dark:bg-black/20 text-slate-600 dark:text-muted-foreground border-slate-200 dark:border-border dark:border-slate-700 hover:border-primary/50"
                              )}
                            >
                              {tag}
                            </button>
                          )) : [
                            t("extraFiles.greatAudio"), t("extraFiles.smoothVideo"), t("extraFiles.easyToUse"), t("extraFiles.stableConnection")
                          ].map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => handleTagToggle(tag)}
                              className={cn(
                                "text-[11px] sm:text-xs px-3 py-1.5 rounded-full border transition-colors",
                                selectedTags.includes(tag)
                                  ? "bg-amber-500 text-white border-amber-500 font-medium shadow-sm"
                                  : "bg-card dark:bg-black/20 text-slate-600 dark:text-muted-foreground border-slate-200 dark:border-border dark:border-slate-700 hover:border-amber-500/50"
                              )}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>

                      <textarea
                        placeholder={t("extraFiles.tellUsMore")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="w-full resize-none rounded-xl border border-slate-200 dark:border-border dark:border-slate-700 bg-card dark:bg-black/20 px-4 py-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary min-h-[80px] shadow-inner"
                      />

                      <Button onClick={handleSubmitFeedback} className="w-full h-11 text-base font-medium shadow-md">
                        <Send className="h-4 w-4 mr-2" />
                        {t("extraFiles.submitFeedback")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Spacer if no feedback section */}
          {isError && <div className="mt-10" />}
          </div>

          {/* Action Buttons — outside the scrolling half, so they are always
              on screen however long the feedback gets. */}
          <div className={cn("flex shrink-0 flex-col gap-3", !isError && "mt-4")}>
            {onRejoin && (
              <Button
                onClick={onRejoin}
                variant={isError ? "default" : "default"}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t("extraFiles.tryToRejoin")}
              </Button>
            )}
            {onGoHome && (
              <Button
                variant={isError ? "ghost" : "ghost"}
                onClick={onGoHome}
                className="w-full"
              >
                <Home className="w-4 h-4 mr-2" />
                {t("extraFiles.backToDashboard")}
              </Button>
            )}
          </div>

        </div>
      </div>
    </RibbonBackground>
  )
}